/**
 * The ONLY sanctioned source of status / trading colours in the app.
 *
 * Never write raw Tailwind palette utilities (`bg-emerald-500`, `text-red-600`,
 * `bg-gray-500/10`, `text-amber-500`, ...) in a page or component. They don't
 * adapt to light/dark and duplicate the Aurora palette. Instead, map your
 * status enum to a {@link StatusTone} and read the class strings from here.
 *
 * Tones map onto the semantic CSS tokens defined in globals.css:
 *   profit / loss      -> trading gain / loss (green / rose)
 *   destructive        -> hard failure / rejected / error
 *   warning            -> partial / trial / caution (amber)
 *   info               -> in-flight / received / neutral-positive (brand teal)
 *   neutral            -> unknown / disabled / muted
 *
 * Example:
 *   const TONE: Record<SignalStatus, StatusTone> = {
 *     Received: "info", RoutedAll: "profit", RoutedPartial: "warning",
 *     Rejected: "warning", RoutedNone: "destructive", Failed: "destructive",
 *   };
 *   <Badge className={statusChip(TONE[status])}>{status}</Badge>
 */
export type StatusTone =
  | "profit"
  | "loss"
  | "destructive"
  | "warning"
  | "info"
  | "neutral";

export interface StatusToneClasses {
  /** Foreground text colour. */
  text: string;
  /** Tinted fill (10% alpha). */
  bg: string;
  /** Hairline border (20% alpha). */
  border: string;
  /** Solid dot / indicator fill. */
  dot: string;
  /** Ready-made badge/chip lockup: tinted fill + text + border. */
  chip: string;
}

export const STATUS_TOKENS: Record<StatusTone, StatusToneClasses> = {
  profit: {
    text: "text-profit",
    bg: "bg-profit/10",
    border: "border-profit/20",
    dot: "bg-profit",
    chip: "bg-profit/10 text-profit border-profit/20",
  },
  loss: {
    text: "text-loss",
    bg: "bg-loss/10",
    border: "border-loss/20",
    dot: "bg-loss",
    chip: "bg-loss/10 text-loss border-loss/20",
  },
  destructive: {
    text: "text-destructive",
    bg: "bg-destructive/10",
    border: "border-destructive/20",
    dot: "bg-destructive",
    chip: "bg-destructive/10 text-destructive border-destructive/20",
  },
  warning: {
    text: "text-warning",
    bg: "bg-warning/10",
    border: "border-warning/20",
    dot: "bg-warning",
    chip: "bg-warning/10 text-warning border-warning/20",
  },
  info: {
    text: "text-primary",
    bg: "bg-primary/10",
    border: "border-primary/20",
    dot: "bg-primary",
    chip: "bg-primary/10 text-primary border-primary/20",
  },
  neutral: {
    text: "text-muted-foreground",
    bg: "bg-muted",
    border: "border-border",
    dot: "bg-muted-foreground/50",
    chip: "bg-muted text-muted-foreground border-border",
  },
};

/** Ready-made badge/chip class string (tinted fill + text + border) for a tone. */
export function statusChip(tone: StatusTone): string {
  return STATUS_TOKENS[tone].chip;
}

/**
 * Categorical (non-status) colour palette — for distinct-category hues that
 * carry NO status/trading meaning: source kinds, destination kinds, plan tiers,
 * route step types, roles. Backed by the --chart-1..5 tokens
 * (teal / green / violet / amber / rose), so categories stay visually distinct
 * without misusing profit/loss/warning semantics.
 *
 * Example:
 *   const KIND_INDEX = { TradingView: 0, TelegramBot: 1, RestApi: 2, Manual: 3 };
 *   <Badge className={categoryTint(KIND_INDEX[kind])}>{kind}</Badge>
 */
export const CATEGORY_TINTS = [
  "bg-chart-1/10 text-chart-1 border-chart-1/20",
  "bg-chart-2/10 text-chart-2 border-chart-2/20",
  "bg-chart-3/10 text-chart-3 border-chart-3/20",
  "bg-chart-4/10 text-chart-4 border-chart-4/20",
  "bg-chart-5/10 text-chart-5 border-chart-5/20",
] as const;

/** Categorical chip class string for a category index (wraps around the 5 tints). */
export function categoryTint(index: number): string {
  const n = CATEGORY_TINTS.length;
  return CATEGORY_TINTS[((index % n) + n) % n];
}
